// Events
import { MODAL_CLOSE, MODAL_OPEN } from 'events';
import { component } from 'picoapp';
import Swiper, {
  A11y,
  Autoplay,
  Keyboard,
  Manipulation,
  Mousewheel,
  Navigation,
  Scrollbar,
} from 'swiper';

// Utils
import choozy from 'utils/choozy';
import { mobileOnlyFunction } from 'utils/device';
import { hide, show } from 'utils/node';
import { dispatch } from 'utils/theme';

Swiper.use([A11y, Autoplay, Navigation, Manipulation, Mousewheel, Scrollbar, Keyboard]);

const KEY_ENTER = 'Enter';
const KEY_SPACE = ' ';
const MEDIA_TYPE_VIDEO = 'video';

export default component(async (node, ctx) => {
  const { sliderMain, thumbsBar, scrollbar, slide, thumbnail, overlay, moreButtonThumb } =
    choozy(node);

  const slides = [].concat(slide);
  const thumbnails = [].concat(thumbnail);

  const { fullscreenModalId } = node.dataset;

  const carouselVideo = (slideElement) => slideElement.querySelector('[data-carousel-video]');

  const syncCarouselVideo = (activeIndex) => {
    slides.forEach((slideElement) => {
      const video = carouselVideo(slideElement);
      if (!video) return;

      const poster = slideElement.querySelector('[data-carousel-poster]');
      const shouldPlay =
        +slideElement.dataset.slide === activeIndex &&
        slideElement.dataset.slideType === MEDIA_TYPE_VIDEO;

      if (!shouldPlay) {
        video.pause();
        video.currentTime = 0;
        video.classList.add('hidden');
        poster?.classList.remove('hidden');
        return;
      }

      video.muted = true;
      poster?.classList.add('hidden');
      video.classList.remove('hidden');

      const start = video.play();

      if (!start) return;

      start.catch((error) => {
        if (error?.name === 'AbortError') return;

        video.classList.add('hidden');
        poster?.classList.remove('hidden');
      });
    });
  };

  const openFullscreen = (activeSlide) => {
    slides.forEach((slideElement) => carouselVideo(slideElement)?.pause());

    ctx.emit(MODAL_OPEN, null, {
      id: fullscreenModalId,
      props: { activeSlide },
    });

    dispatch('pdp-enlarge-main-image');
  };

  const carousel = new Swiper(sliderMain, {
    slidesPerView: 1,
    speed: 200,
    breakpoints: {
      1024: {
        speed: 1300,
      },
    },
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    mousewheel: {
      forceToAxis: true,
    },
    watchSlidesProgress: true,
    scrollbar: {
      el: scrollbar,
      draggable: true,
    },
  });

  const thumbsCarousel = new Swiper(thumbsBar, {
    slidesPerView: 7,
    direction: 'vertical',
    spaceBetween: 7,
  });

  carousel.on('slideChange', ({ activeIndex }) => {
    thumbsCarousel.slideTo(activeIndex);

    thumbnails.forEach((el) => {
      el.classList[+el.dataset.slideRef === activeIndex ? 'add' : 'remove']('active');
    });

    dispatch('pdp-change-main-image', { index: activeIndex });

    syncCarouselVideo(activeIndex);
  });

  thumbnails.forEach((el) => {
    el.addEventListener('click', () => {
      carousel.slideTo(+el.dataset.slideRef);
    });
  });

  syncCarouselVideo(carousel.activeIndex);

  ctx.on(MODAL_CLOSE, (state, { id }) => {
    if (id !== fullscreenModalId) return;

    syncCarouselVideo(carousel.activeIndex);
  });

  node.addEventListener('slider:touchStart', (e) => {
    if (e.detail.sliderEl === node) e.stopPropagation();
  });

  node.addEventListener('slider:touchEnd', (e) => {
    if (e.detail.sliderEl === node) e.stopPropagation();
  });

  node.addEventListener('video:play', () => {
    hide(overlay, scrollbar);
    mobileOnlyFunction(() => hide(thumbsBar));
  });

  node.addEventListener('video:ended', () => {
    show(overlay, scrollbar);
    mobileOnlyFunction(() => show(thumbsBar));
  });

  slides.forEach((slideElement) => {
    const index = parseInt(slideElement.dataset.slide, 10);

    const openSlide = () => {
      openFullscreen(index);
    };

    slideElement.addEventListener('click', openSlide);

    slideElement.addEventListener('keydown', (event) => {
      const isActivationKey = event.key === KEY_ENTER || event.key === KEY_SPACE;

      if (!isActivationKey) return;

      event.preventDefault();

      openSlide();
    });
  });

  moreButtonThumb?.addEventListener('click', () => {
    openFullscreen(6);
  });
});
